import { clobMarketToMarket } from './adapter';
import { clobClient } from './client';
import type { OrderbookSnapshot, ClobMarket, ClobOrder, ClobTrade, EscrowBalance } from './types';
import { CLOB_READY, REFETCH_INTERVAL_FAST, REFETCH_INTERVAL_MEDIUM, CLOB_WS_URL } from '@/client/confs/app';
import type { Market } from '@/libs/sdk/api';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useEffect, useRef, useState, useMemo } from 'react';

// CLOB_READY (flag on AND URL configured) gates every query below: when the
// flag is off or the tenant never configured VITE_CLOB_API_URL (e.g. AIP
// prod) this hook file must stay fully idle — no fetch, no polling, no
// throw/retry churn against the disabled client.
export const useClobMarkets = () => {
  return useQuery<ClobMarket[]>({
    queryKey: ['clob-markets'],
    queryFn: () => clobClient.getMarkets(),
    enabled: CLOB_READY,
    refetchInterval: REFETCH_INTERVAL_MEDIUM,
  });
};

export const useClobOrderbook = (tokenId?: string) => {
  return useQuery<OrderbookSnapshot>({
    queryKey: ['clob-orderbook', tokenId],
    queryFn: () => clobClient.getOrderbook(tokenId!),
    enabled: CLOB_READY && !!tokenId,
    refetchInterval: REFETCH_INTERVAL_FAST,
  });
};

export const useClobPrice = (tokenId?: string) => {
  return useQuery<{ bid: string; ask: string }>({
    queryKey: ['clob-price', tokenId],
    queryFn: () => clobClient.getPrice(tokenId!),
    enabled: CLOB_READY && !!tokenId,
    refetchInterval: REFETCH_INTERVAL_FAST,
  });
};

export const useClobOrders = (enabled = true) => {
  return useQuery<ClobOrder[]>({
    queryKey: ['clob-orders'],
    queryFn: () => clobClient.getOrders(),
    enabled: CLOB_READY && enabled,
    refetchInterval: REFETCH_INTERVAL_MEDIUM,
  });
};

export const useClobTrades = (enabled = true) => {
  return useQuery<ClobTrade[]>({
    queryKey: ['clob-trades'],
    queryFn: () => clobClient.getTrades(),
    enabled: CLOB_READY && enabled,
    refetchInterval: REFETCH_INTERVAL_MEDIUM,
  });
};

export const useClobEscrowBalance = (enabled = true) => {
  return useQuery<EscrowBalance>({
    queryKey: ['clob-escrow-balance'],
    queryFn: () => clobClient.getEscrowBalance(),
    enabled: CLOB_READY && enabled,
    refetchInterval: REFETCH_INTERVAL_MEDIUM,
  });
};

export const useClobHealth = () => {
  return useQuery({
    queryKey: ['clob-health'],
    queryFn: () => clobClient.health(),
    enabled: CLOB_READY,
    refetchInterval: 30_000,
  });
};

export const useClobWebSocket = (tokenId?: string) => {
  const [orderbook, setOrderbook] = useState<OrderbookSnapshot | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!tokenId || !CLOB_WS_URL) return;

    const openWs = () => {
      const ws = new WebSocket(CLOB_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'subscribe', tokenId }));
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'depth' || data.type === 'orderbook') {
            setOrderbook({
              bids: data.bids || [],
              asks: data.asks || [],
              timestamp: data.timestamp || Date.now(),
            });
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        reconnectTimer.current = setTimeout(openWs, 3000);
      };
    };

    openWs();

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [tokenId]);

  return orderbook;
};

/**
 * Fetch CLOB markets + their prices, return as SDK Market[] objects
 * ready for MarketCard rendering.
 */
export const useClobMarketsAsMarket = (): {
  data: Market[];
  isLoading: boolean;
} => {
  const { data: clobMarkets, isLoading: marketsLoading } = useClobMarkets();

  // Fetch price for each CLOB market's first token
  const priceQueries = useQueries({
    queries: (clobMarkets ?? []).map(cm => ({
      queryKey: ['clob-price', cm.tokens?.[0]?.tokenId || cm.conditionId],
      queryFn: () => clobClient.getPrice(cm.tokens?.[0]?.tokenId || cm.conditionId),
      enabled: CLOB_READY && !!cm.conditionId,
      refetchInterval: REFETCH_INTERVAL_MEDIUM,
      staleTime: 10_000,
    })),
  });

  const data = useMemo(() => {
    if (!clobMarkets) return [];
    return clobMarkets.map((cm, i) => {
      const price = priceQueries[i]?.data ?? null;
      return clobMarketToMarket(cm, price);
    });
  }, [clobMarkets, priceQueries]);

  return { data, isLoading: marketsLoading };
};
